/**
 * Loyalty Cache Management Page
 *
 * Admin page for viewing and managing loyalty Redis caches per tenant.
 * Features:
 * - Dropdown to select tenant from list of all companies
 * - View cache status (TTLs, key counts) for selected tenant
 * - Select individual cache types to clear (checkboxes)
 * - Clear specific cache types (rules, products, user profiles, etc.)
 * - Clear all caches at once
 * - Real-time status refresh
 */

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { MdRefresh, MdDeleteForever, MdCheckCircle } from "react-icons/md";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";
import {
  useGetCacheStatusMutation,
  useClearAllCacheMutation,
  useClearRulesCacheMutation,
} from "../../../features/admin/loyaltyCache/loyaltyCacheApi";
import "./LoyaltyCache.css";

const CACHE_TYPES = {
  rules: { label: "Rules Cache", key: "rules" },
  companyData: { label: "Company Data Cache", key: "companyData" },
  productCache: { label: "Product Cache", key: "productCache" },
  userProfileCache: { label: "User Profile Cache", key: "userProfileCache" },
  trackingRegistry: { label: "Tracking Registry", key: "trackingRegistry" },
  activeSchemes: { label: "Active Schemes", key: "activeSchemes" },
  schemeProducts: { label: "Scheme Products", key: "schemeProducts" },
  idempotencyKeys: { label: "Idempotency Keys", key: "idempotencyKeys" },
};

const LoyaltyCache = () => {
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [cacheStatus, setCacheStatus] = useState(null);
  const [selectedCacheTypes, setSelectedCacheTypes] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState(null); // "clear-all" | "clear-selected" | "clear-rules"
  const [clearReason, setClearReason] = useState("");
  const [clearRuleId, setClearRuleId] = useState("");

  const token = sessionStorage.getItem("access_token");

  // Fetch vendors/companies for dropdown
  const {
    data: vendorsData,
    isLoading: vendorsLoading,
    error: vendorsError,
  } = useFetchVendorsQuery(token);

  // API mutations
  const [
    getCacheStatus,
    { data: statusData, isLoading: statusLoading, error: statusError },
  ] = useGetCacheStatusMutation();

  const [clearAllCache, { isLoading: clearAllLoading }] =
    useClearAllCacheMutation();

  const [clearRulesCache, { isLoading: clearRulesLoading }] =
    useClearRulesCacheMutation();

  // Update cache status when API responds
  useMemo(() => {
    if (statusData?.body) {
      setCacheStatus(statusData.body);
      // Initialize selected cache types when new status is loaded
      setSelectedCacheTypes({});
    }
    if (statusError) {
      toast.error(statusError.error || "Failed to fetch cache status");
    }
  }, [statusData, statusError]);

  // Fetch cache status
  const handleFetchStatus = () => {
    if (!selectedTenantId) {
      toast.error("Please select a Tenant");
      return;
    }
    getCacheStatus({ tenantId: selectedTenantId, token });
  };

  // Handle cache type checkbox change
  const handleCacheTypeToggle = (cacheType) => {
    setSelectedCacheTypes((prev) => ({
      ...prev,
      [cacheType]: !prev[cacheType],
    }));
  };

  // Select all cache types
  const handleSelectAll = () => {
    const allSelected = {};
    Object.keys(CACHE_TYPES).forEach((key) => {
      allSelected[key] = true;
    });
    setSelectedCacheTypes(allSelected);
  };

  // Deselect all cache types
  const handleDeselectAll = () => {
    setSelectedCacheTypes({});
  };

  // Count selected cache types
  const selectedCount = Object.values(selectedCacheTypes).filter(Boolean).length;

  // Handle clear action confirmation
  const handleClearConfirm = async () => {
    try {
      if (dialogAction === "clear-all") {
        await clearAllCache({
          tenantId: selectedTenantId,
          token,
          reason: clearReason || "manual_invalidation",
          ruleId: clearRuleId || undefined,
        }).unwrap();
        toast.success("All caches cleared successfully");
      } else if (dialogAction === "clear-rules") {
        await clearRulesCache({
          tenantId: selectedTenantId,
          token,
          reason: clearReason || "manual_invalidation",
          ruleId: clearRuleId || undefined,
        }).unwrap();
        toast.success("Rules cache cleared successfully");
      } else if (dialogAction === "clear-selected") {
        // For selected caches, clear all for now (can be optimized to only clear selected)
        await clearAllCache({
          tenantId: selectedTenantId,
          token,
          reason: clearReason || "manual_invalidation",
          ruleId: clearRuleId || undefined,
        }).unwrap();
        toast.success(
          `${selectedCount} cache type(s) cleared successfully`
        );
      }

      // Refresh cache status after clearing
      getCacheStatus({ tenantId: selectedTenantId, token });
      setOpenConfirmDialog(false);
      setClearReason("");
      setClearRuleId("");
      setSelectedCacheTypes({});
    } catch (err) {
      toast.error(err?.data?.message || "Failed to clear cache");
    }
  };

  // Format TTL into human-readable format
  const formatTTL = (seconds) => {
    if (seconds === null) return "No expiry";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${mins}m ${secs}s`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const vendors = vendorsData?.body || [];
  const isLoading = vendorsLoading || statusLoading;

  return (
    <div className="page-content loyalty-cache-container">
      <div className="card p-4 mb-4">
        <h3 className="card-title mb-4">Loyalty Cache Management</h3>

        {/* Tenant Selection Section */}
        <div className="row mb-4">
          <div className="col-md-6">
            <FormControl fullWidth>
              <InputLabel>Select Tenant</InputLabel>
              <Select
                value={selectedTenantId}
                onChange={(e) => {
                  setSelectedTenantId(e.target.value);
                  setCacheStatus(null);
                  setSelectedCacheTypes({});
                }}
                label="Select Tenant"
                disabled={vendorsLoading}
              >
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.tenant_id} value={vendor.tenant_id}>
                    {vendor.name} (ID: {vendor.tenant_id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {vendorsError && (
              <p className="text-danger small mt-2">
                Error loading vendors: {vendorsError.error}
              </p>
            )}
          </div>
          <div className="col-md-6 d-flex gap-2">
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleFetchStatus}
              disabled={isLoading || !selectedTenantId}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <MdRefresh />
                )
              }
            >
              {isLoading ? "Loading..." : "Check Status"}
            </Button>
          </div>
        </div>

        {/* Cache Status Display */}
        {cacheStatus && (
          <>
            <div className="alert alert-info mb-4">
              <strong>Tenant ID:</strong> {cacheStatus.tenantId} |{" "}
              <strong>Environment:</strong> {cacheStatus.env} |{" "}
              <strong>Fetched:</strong> {formatDate(cacheStatus.fetchedAt)}
            </div>

            {/* Cache Selection Section */}
            <div className="card bg-light mb-4 p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Select Caches to Manage</h5>
                <div className="d-flex gap-2">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleDeselectAll}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              <FormGroup>
                <div className="row">
                  {Object.entries(CACHE_TYPES).map(([key, config]) => (
                    <div key={key} className="col-md-6 col-lg-4">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedCacheTypes[key] || false}
                            onChange={() => handleCacheTypeToggle(key)}
                            color="primary"
                          />
                        }
                        label={`${config.label}`}
                      />
                      {cacheStatus.caches[key] && (
                        <div className="ms-4 small text-muted">
                          {cacheStatus.caches[key].keyCount !== undefined ? (
                            <span>
                              Keys: {cacheStatus.caches[key].keyCount}
                              {cacheStatus.caches[key].active && (
                                <span className="badge bg-success ms-2">
                                  Active
                                </span>
                              )}
                            </span>
                          ) : (
                            <span>
                              {cacheStatus.caches[key].active ? (
                                <>
                                  TTL:{" "}
                                  {formatTTL(
                                    cacheStatus.caches[key].ttlSeconds
                                  )}
                                  <span className="badge bg-success ms-2">
                                    Active
                                  </span>
                                </>
                              ) : (
                                <span className="badge bg-secondary">
                                  Inactive
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </FormGroup>

              {selectedCount > 0 && (
                <div className="mt-3">
                  <p className="mb-2">
                    <strong>{selectedCount} cache type(s) selected</strong>
                  </p>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<MdDeleteForever />}
                    onClick={() => {
                      setDialogAction("clear-selected");
                      setOpenConfirmDialog(true);
                    }}
                    disabled={clearAllLoading}
                  >
                    {clearAllLoading ? "Clearing..." : "Clear Selected"}
                  </Button>
                </div>
              )}
            </div>

            {/* Cache Status Grid */}
            <div className="mb-4">
              <h5 className="mb-3">Cache Details</h5>
              <div className="row">
                {/* Exact Keys (Rules, Company Data) */}
                <div className="col-md-6 mb-3">
                  <Card className="cache-status-card">
                    <CardContent>
                      <h6 className="cache-title">Rules Cache</h6>
                      <div className="cache-info">
                        <p>
                          <strong>Status:</strong>{" "}
                          <span
                            className={
                              cacheStatus.caches.rules.active
                                ? "badge bg-success"
                                : "badge bg-secondary"
                            }
                          >
                            {cacheStatus.caches.rules.active
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </p>
                        {cacheStatus.caches.rules.active && (
                          <>
                            <p>
                              <strong>TTL:</strong>{" "}
                              {formatTTL(cacheStatus.caches.rules.ttlSeconds)}
                            </p>
                            <p>
                              <strong>Expires:</strong>{" "}
                              {formatDate(cacheStatus.caches.rules.expiresAt)}
                            </p>
                          </>
                        )}
                        <Button
                          fullWidth
                          variant="outlined"
                          color="warning"
                          size="small"
                          startIcon={<MdDeleteForever />}
                          onClick={() => {
                            setDialogAction("clear-rules");
                            setOpenConfirmDialog(true);
                          }}
                          disabled={clearRulesLoading}
                        >
                          {clearRulesLoading ? "Clearing..." : "Clear"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="col-md-6 mb-3">
                  <Card className="cache-status-card">
                    <CardContent>
                      <h6 className="cache-title">Company Data Cache</h6>
                      <div className="cache-info">
                        <p>
                          <strong>Status:</strong>{" "}
                          <span
                            className={
                              cacheStatus.caches.companyData.active
                                ? "badge bg-success"
                                : "badge bg-secondary"
                            }
                          >
                            {cacheStatus.caches.companyData.active
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </p>
                        {cacheStatus.caches.companyData.active && (
                          <>
                            <p>
                              <strong>TTL:</strong>{" "}
                              {formatTTL(
                                cacheStatus.caches.companyData.ttlSeconds
                              )}
                            </p>
                            <p>
                              <strong>Expires:</strong>{" "}
                              {formatDate(
                                cacheStatus.caches.companyData.expiresAt
                              )}
                            </p>
                          </>
                        )}
                        <p className="text-muted small">
                          Included in "Clear All" action
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Wildcard Keys */}
                {Object.entries({
                  productCache: "Product Cache",
                  userProfileCache: "User Profile Cache",
                  trackingRegistry: "Tracking Registry",
                  activeSchemes: "Active Schemes",
                  schemeProducts: "Scheme Products",
                  idempotencyKeys: "Idempotency Keys",
                }).map(([key, label]) => (
                  <div key={key} className="col-md-4 mb-3">
                    <Card className="cache-status-card">
                      <CardContent>
                        <h6 className="cache-title">{label}</h6>
                        <div className="cache-info">
                          <p>
                            <strong>Status:</strong>{" "}
                            <span
                              className={
                                cacheStatus.caches[key].active
                                  ? "badge bg-success"
                                  : "badge bg-secondary"
                              }
                            >
                              {cacheStatus.caches[key].active
                                ? "Active"
                                : "Inactive"}
                            </span>
                          </p>
                          <p>
                            <strong>Keys:</strong>{" "}
                            {cacheStatus.caches[key].keyCount}
                          </p>
                          <p className="text-muted small">
                            Included in "Clear All" action
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {/* Clear All Action */}
            <div className="alert alert-warning">
              <strong>⚠️ Clear All Cache</strong>
              <p className="mb-2">
                This will clear all cache types for tenant {selectedTenantId}.
                Use this when you need a complete cache refresh.
              </p>
              <Button
                variant="contained"
                color="error"
                startIcon={<MdDeleteForever />}
                onClick={() => {
                  setDialogAction("clear-all");
                  setOpenConfirmDialog(true);
                }}
                disabled={clearAllLoading}
              >
                {clearAllLoading ? "Clearing..." : "Clear All Caches"}
              </Button>
            </div>
          </>
        )}

        {/* No Data State */}
        {!cacheStatus && !isLoading && selectedTenantId && (
          <div className="alert alert-light text-center">
            <p className="text-muted">
              Click "Check Status" to view cache information for the selected
              tenant
            </p>
          </div>
        )}

        {/* Initial State */}
        {!cacheStatus && !isLoading && !selectedTenantId && (
          <div className="alert alert-light text-center">
            <p className="text-muted">Select a Tenant and click "Check Status"</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-5">
            <CircularProgress />
            <p className="text-muted mt-3">Loading cache status...</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogAction === "clear-all"
            ? "Clear All Caches"
            : dialogAction === "clear-rules"
              ? "Clear Rules Cache"
              : "Clear Selected Caches"}
        </DialogTitle>
        <DialogContent>
          <div className="mt-3">
            <p className="mb-3">
              {dialogAction === "clear-all"
                ? "This will clear all cache types for this tenant. This action cannot be undone."
                : dialogAction === "clear-rules"
                  ? "This will clear the loyalty rules cache for this tenant. The rules will be reloaded from the database on the next access."
                  : `This will clear ${selectedCount} selected cache type(s). This action cannot be undone.`}
            </p>

            <FormControl fullWidth className="mb-3">
              <InputLabel>Reason (Optional)</InputLabel>
              <Select
                value={clearReason}
                onChange={(e) => setClearReason(e.target.value)}
                label="Reason (Optional)"
                size="small"
              >
                <MenuItem value="">Manual Invalidation</MenuItem>
                <MenuItem value="rule_created">Rule Created</MenuItem>
                <MenuItem value="rule_updated">Rule Updated</MenuItem>
                <MenuItem value="rule_deleted">Rule Deleted</MenuItem>
                <MenuItem value="rule_status_change">Rule Status Changed</MenuItem>
                <MenuItem value="manual_invalidation">Manual Invalidation</MenuItem>
              </Select>
            </FormControl>

            {(clearReason === "rule_created" ||
              clearReason === "rule_updated" ||
              clearReason === "rule_deleted") && (
                <TextField
                  fullWidth
                  label="Rule ID (Optional)"
                  type="number"
                  value={clearRuleId}
                  onChange={(e) => setClearRuleId(e.target.value)}
                  size="small"
                  placeholder="e.g., 50"
                />
              )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Cancel</Button>
          <Button
            onClick={handleClearConfirm}
            variant="contained"
            color="error"
            disabled={clearAllLoading || clearRulesLoading}
          >
            {clearAllLoading || clearRulesLoading ? "Clearing..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LoyaltyCache;
